import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {BehaviorSubject, Observable} from 'rxjs';
import {DtrumApi} from '@dynatrace/dtrum-api-types/dtrum';

declare global {
  interface Window {
    dtrum?: DtrumApi;
  }
}

@Injectable({
  providedIn: 'root',
})
export class DynatraceConsentService implements OnDestroy {
  private readonly consentKey = 'dynatrace.sap.com';
  private readonly optoutDomainsKey = 'optout_domains';
  private consentSubject: BehaviorSubject<boolean>;
  public consent$: Observable<boolean>;
  private storageListener?: (event: StorageEvent) => void;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    const initialConsent = this.checkConsent();
    this.consentSubject = new BehaviorSubject<boolean>(initialConsent);
    this.consent$ = this.consentSubject.asObservable();

    // Set initial dtrum state if dtrum is already available
    if (isPlatformBrowser(this.platformId)) {
      this.updateDtrumState(initialConsent);

      // Listen for storage changes (cross-tab consent updates)
      this.storageListener = (event: StorageEvent) => {
        if (event.key === this.optoutDomainsKey) {
          const newConsent = this.checkConsent();
          if (newConsent !== this.consentSubject.value) {
            this.consentSubject.next(newConsent);
            this.updateDtrumState(newConsent);
          }
        }
      };
      window.addEventListener('storage', this.storageListener);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
  }

  /**
   * Check if user has given consent for Dynatrace tracking
   */
  hasConsent(): boolean {
    return this.consentSubject.value;
  }

  private checkConsent(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const optoutDomainsRaw = localStorage.getItem(this.optoutDomainsKey);
      if (!optoutDomainsRaw) {
        return false;
      }

      const optoutDomains = JSON.parse(optoutDomainsRaw);
      const value = optoutDomains['Functional Cookies']?.domains?.[this.consentKey];

      // Handle both string "1" and number 1
      return value === '1' || value === 1;
    } catch (e) {
      console.error('[DynatraceConsent] Error checking Dynatrace consent:', e);
      return false;
    }
  }

  private updateDtrumState(hasConsent: boolean): void {
    if (typeof window !== 'undefined' && window.dtrum) {
      if (hasConsent) {
        window.dtrum.enable();
      } else {
        window.dtrum.disable();
      }
    }
  }
}
